import { useState } from "react";
import { Text } from "components/Typography/text.styled";
import { FontSize } from "config/font";
import { Color } from "config/colors";
import {
  ToggleContainer,
  ToggleLabel,
  ToggleSwitch,
  ToggleOption,
  ToggleThumb,
} from "./toggle.styled";

interface ToggleWithLabelProps {
  label: string;
  leftLabel: string;
  rightLabel: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  required?: boolean;
  hasError?: boolean;
  errorText?: string;
  disabled?: boolean;
  marginLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
}

export const ToggleWithLabel: React.FC<ToggleWithLabelProps> = (props) => {
  const [internalValue, setInternalValue] = useState<boolean>(
    props.value ?? false
  );
  const controlled = props.value !== undefined;
  const currentValue = controlled ? (props.value as boolean) : internalValue;

  const handleToggle = () => {
    if (props.disabled) return;
    const next = !currentValue;
    if (!controlled) setInternalValue(next);
    props.onChange?.(next);
  };

  const selectLeft = () => {
    if (props.disabled) return;
    if (!controlled) setInternalValue(false);
    props.onChange?.(false);
  };

  const selectRight = () => {
    if (props.disabled) return;
    if (!controlled) setInternalValue(true);
    props.onChange?.(true);
  };

  return (
    <ToggleContainer
      marginLeft={props.marginLeft}
      marginRight={props.marginRight}
      marginTop={props.marginTop}
      marginBottom={props.marginBottom}
    >
      <ToggleLabel required={props.required}>{props.label}</ToggleLabel>

      <ToggleSwitch
        role="switch"
        aria-checked={currentValue}
        aria-invalid={props.hasError || undefined}
        aria-disabled={props.disabled || undefined}
        tabIndex={props.disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (props.disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
          if (e.key === "ArrowLeft") selectLeft();
          if (e.key === "ArrowRight") selectRight();
        }}
        disabled={props.disabled}
        hasError={props.hasError}
        data-active={currentValue}
      >
        <ToggleOption
          data-active={!currentValue}
          onClick={(e) => {
            e.stopPropagation();
            selectLeft();
          }}
        >
          {props.leftLabel}
        </ToggleOption>

        <ToggleThumb data-active={currentValue} />

        <ToggleOption
          data-active={currentValue}
          onClick={(e) => {
            e.stopPropagation();
            selectRight();
          }}
        >
          {props.rightLabel}
        </ToggleOption>
      </ToggleSwitch>

      {props.errorText && (
        <Text
          fontSize={FontSize.Tiny}
          color={Color.LighterDestructive}
          style={{ marginTop: 6, width: 280 }}
        >
          {props.errorText}
        </Text>
      )}
    </ToggleContainer>
  );
};
